import { useEffect, useState } from "react";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell,
} from "recharts";
import { Clock, Swords, Skull, Trophy, Target, TrendingUp } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Loader } from "@/components/common/Loader";
import { api } from "@/lib/api";

export default function Analytics() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/analytics").then(({ data }) => setData(data)); }, []);
    if (!data) return <Loader label="Loading analytics" />;

    const t = data.totals;
    const tip = { background: "#1A1A1A", border: "1px solid #333", borderRadius: 2, fontSize: 12 };

    return (
        <div className="ww-fade-up">
            <PageHeader title="Analytics Center" subtitle="Lifetime performance across all your tracked games." />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard testid="an-hours" label="Hours Played" value={t.hours} icon={Clock} accent="#00C2FF" />
                <StatCard testid="an-kd" label="K / D" value={t.kd} sub={`${t.kills} kills`} icon={Swords} accent="#FF3366" />
                <StatCard testid="an-winrate" label="Win Rate" value={`${t.win_rate}%`} sub={`${t.matches} matches`} icon={Trophy} accent="#00E676" />
                <StatCard testid="an-tops" label="Top Placements" value={t.top_placements} icon={Target} accent="#FFB000" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard testid="an-kills" label="Total Kills" value={t.kills} icon={Swords} accent="#00C2FF" />
                <StatCard testid="an-deaths" label="Total Deaths" value={t.deaths} icon={Skull} accent="#FF3366" />
                <StatCard testid="an-matches" label="Matches" value={t.matches} icon={Target} accent="#00E676" />
                <StatCard testid="an-score" label="Improvement Score" value={t.improvement_score} icon={TrendingUp} accent="#FFB000" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-4">Most Played Games</h3>
                    <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                            <BarChart data={data.by_game}>
                                <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="game" tick={{ fill: "#A3A3A3", fontSize: 11 }} />
                                <YAxis tick={{ fill: "#666", fontSize: 10 }} width={28} />
                                <Tooltip contentStyle={tip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                                    {data.by_game.map((g, i) => <Cell key={i} fill={g.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-4">K/D Over Recent Matches</h3>
                    <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                            <LineChart data={data.trend}>
                                <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} minTickGap={20} />
                                <YAxis tick={{ fill: "#666", fontSize: 10 }} width={28} />
                                <Tooltip contentStyle={tip} />
                                <Line type="monotone" dataKey="kd" stroke="#FF3366" strokeWidth={2.5} dot={false} />
                                <Line type="monotone" dataKey="kills" stroke="#00C2FF" strokeWidth={1.5} dot={false} opacity={0.6} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}
